'use client';
import React from 'react';
import Link from 'next/link';
import { ArrowRight, Lock, PlayCircle, CheckCircle2 } from 'lucide-react';
import Badge from '@/components/ui/Badge';

const courses = [
  {
    id: 'course-sd-001',
    name: 'Intro to Coding',
    wing: 'Intermediate',
    wingVariant: 'intermediate' as const,
    progress: 68,
    lessonsCompleted: 11,
    totalLessons: 16,
    nextLesson: 'Functions & Loops',
    status: 'active',
    color: 'bg-blue-500',
  },
  {
    id: 'course-sd-002',
    name: 'Earth & Space Science',
    wing: 'Intermediate',
    wingVariant: 'intermediate' as const,
    progress: 42,
    lessonsCompleted: 7,
    totalLessons: 17,
    nextLesson: 'The Solar Wind',
    status: 'active',
    color: 'bg-sky-400',
  },
  {
    id: 'course-sd-003',
    name: 'Advanced Mathematics',
    wing: 'Advanced',
    wingVariant: 'advanced' as const,
    progress: 0,
    lessonsCompleted: 0,
    totalLessons: 22,
    nextLesson: 'Awaiting parent approval',
    status: 'locked',
    color: 'bg-purple-500',
  },
  {
    id: 'course-sd-004',
    name: 'Logic & Problem Solving',
    wing: 'Intermediate',
    wingVariant: 'intermediate' as const,
    progress: 91,
    lessonsCompleted: 20,
    totalLessons: 22,
    nextLesson: 'Final Project',
    status: 'nearly-done',
    color: 'bg-emerald-500',
  },
];

export default function StudentCourseProgress() {
  return (
    <div className="bg-card border border-border rounded-2xl p-6">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-bold text-foreground">Active Courses</h2>
        <Link href="/course-catalog" className="flex items-center gap-1 text-sm text-primary font-semibold hover:underline">
          Browse Catalog <ArrowRight size={14} />
        </Link>
      </div>

      <div className="space-y-4">
        {courses.map((course) => (
          <div key={course.id} className="flex items-center gap-4 p-4 rounded-xl bg-muted/40 hover:bg-muted/70 transition-colors group">
            {/* Color dot */}
            <div className={`w-3 h-3 rounded-full shrink-0 ${course.color}`} />

            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <p className="text-sm font-semibold text-foreground truncate">{course.name}</p>
                <Badge variant={course.wingVariant}>{course.wing}</Badge>
                {course.status === 'locked' && (
                  <span className="flex items-center gap-0.5 text-xs text-amber-600">
                    <Lock size={11} /> Locked
                  </span>
                )}
                {course.status === 'nearly-done' && (
                  <span className="flex items-center gap-0.5 text-xs text-emerald-600">
                    <CheckCircle2 size={11} /> Almost done!
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full progress-bar-fill ${course.status === 'locked' ? 'bg-muted-foreground/30' : course.color}`}
                    style={{ width: `${course.progress}%` }}
                  />
                </div>
                <span className="text-xs text-muted-foreground font-tabular shrink-0">{course.lessonsCompleted}/{course.totalLessons}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">Next: {course.nextLesson}</p>
            </div>

            {/* Action */}
            {course.status !== 'locked' && (
              <Link href="/course-catalog" className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                <PlayCircle size={20} className="text-primary" />
              </Link>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}